import { useEffect, useState } from 'react';

/**
 * Width the browser's vertical scrollbar takes out of a scrolling element.
 *
 * Week view stacks three separately-laid-out rows — day headers, the all-day
 * row, and the scrolling time grid — each with its own `repeat(n, 1fr)` grid.
 * Only the time grid scrolls, so on a platform with classic (space-consuming)
 * scrollbars its columns are laid out in a container narrower by this amount
 * and every column drifts left of the header above it, the error compounding
 * across the week.
 *
 * Returns 0 for overlay scrollbars (macOS, most touch devices), which is
 * correct rather than a fallback: they are painted over the content and take
 * no layout width, so nothing needs reserving.
 */
export function useScrollbarWidth(): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      const probe = document.createElement('div');
      probe.style.cssText =
        'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
      document.body.appendChild(probe);
      const measured = probe.offsetWidth - probe.clientWidth;
      document.body.removeChild(probe);
      setWidth(measured);
    };
    measure();
    // Zoom changes and OS scrollbar-preference changes both alter this.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return width;
}
